import axios, { AxiosError, AxiosInstance } from 'axios'
import posthog from 'posthog-js'
import { useAuthStore } from '../stores/authStore'

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling. Also emits a PostHog event
    // when a super-admin acts on a vendor while impersonating, and rewrites
    // the 403 "explicit deny" body into something a user can read.
    this.client.interceptors.response.use(
      (response) => {
        try {
          const method = (response.config.method || '').toLowerCase()
          const state = useAuthStore.getState()
          const impersonation = state.impersonation
          if (MUTATING_METHODS.has(method) && impersonation?.active) {
            posthog.capture('admin_acted_as_vendor', {
              vendor_id: state.user?.vendorId ?? null,
              vendor_name: impersonation.vendorName ?? null,
              path: response.config.url || '',
              method: method.toUpperCase(),
              status: response.status,
            })
          }
        } catch {
          // Telemetry must never break a successful request.
        }
        return response
      },
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          // iter-15: a 401 anywhere in the app used to hard-log the user
          // out, which also nuked the super-admin's real session mid
          // impersonation. That was wrong. Only log out when the 401 came
          // from an actual auth endpoint (token refresh / signin) or when
          // the server explicitly signalled the session token is invalid.
          const url = error.config?.url || ''
          const isAuthEndpoint = url.startsWith('/auth/')
          const body = error.response.data as {
            error?: { code?: string }
            code?: string
          } | undefined
          const code = body?.error?.code || body?.code || ''
          const isTokenFailure =
            code === 'TOKEN_EXPIRED' ||
            code === 'INVALID_TOKEN' ||
            code === 'SESSION_EXPIRED'
          if (isAuthEndpoint || isTokenFailure) {
            useAuthStore.getState().logout()
            window.location.href = '/auth'
          }
          // For all other 401s (business-endpoint permission problems),
          // fall through to the reject below so the caller sees a normal
          // error toast instead of losing their session.
        }
        // Friendlier message for the new READ_ONLY_IMPERSONATOR deny path
        // and for any explicit-deny coming back from the authorizer.
        if (error.response?.status === 403) {
          const body = error.response.data as { message?: string } | undefined
          if (body?.message && body.message.includes('explicit deny')) {
            body.message =
              'This account does not have permission to make changes here. Ask a super-admin or vendor owner to act.'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth endpoints
  auth = {
    signUp: (data: { email: string; password: string; name: string }) =>
      this.client.post('/auth/signup', data),

    signUpVendor: (data: {
      businessName: string;
      legalName: string;
      businessType: 'tours' | 'accommodation' | 'transfers' | 'activities';
      taxId: string;
      registrationNumber?: string;
      website?: string;
      contactPersonName: string;
      contactPersonEmail: string;
      contactPersonPhone: string;
      address: {
        street: string;
        city: string;
        postalCode: string;
        country: string;
      };
      password: string;
      agreementVersion: string;
      agreementAccepted: boolean;
    }) =>
      this.client.post('/auth/signup/vendor', data),

    signIn: (data: { email: string; password: string }) =>
      this.client.post('/auth/signin', data),

    // Issues a fresh access token for the current (still valid) session. The
    // access token is attached automatically by the request interceptor.
    refreshToken: () =>
      this.client.post('/auth/refresh'),

    verifyBokunVendor: (bokunVendorId: string) =>
      this.client.get(`/auth/bokun/verify/${bokunVendorId}`),

    signUpWithBokun: (data: {
      bokunVendorId: number;
      companyName: string;
      legalName: string;
      businessType: 'tours' | 'accommodation' | 'transfers' | 'activities';
      legalEntityType?: 'individual' | 'company';
      registrationNumber?: string;
      taxId?: string;
      website?: string;
      username: string;
      contactPersonName: string;
      contactPersonEmail: string;
      contactPersonPhone: string;
      address: {
        street: string;
        city: string;
        state?: string;
        postalCode: string;
        country: string;
      };
      password: string;
      agreementVersion: string;
      agreementAccepted: boolean;
    }) =>
      this.client.post('/auth/signup/vendor/bokun', data),

    forgotPassword: (data: { email: string }) =>
      this.client.post('/auth/forgot-password', data),

    resetPassword: (data: { token: string; email: string; password: string }) =>
      this.client.post('/auth/reset-password', data),

    acceptInvite: (data: { token: string; email: string; password: string }) =>
      this.client.post('/auth/accept-invite', data),
  }

  // Products endpoints
  products = {
    list: (params?: { vendorId?: string; category?: string; search?: string }) =>
      this.client.get('/products', { params }),

    get: (productId: string) =>
      this.client.get(`/products/${productId}`),

    create: (data: any) =>
      this.client.post('/products', data),

    update: (productId: string, data: any) =>
      this.client.put(`/products/${productId}`, data),

    delete: (productId: string) =>
      this.client.delete(`/products/${productId}`),

    search: (query: string) =>
      this.client.get('/products/search', { params: { q: query } }),

    checkAvailability: (productId: string, params: any) =>
      this.client.get(`/products/${productId}/availability`, { params }),
  }

  // Bookings endpoints
  bookings = {
    list: (vendorId: string, params?: any) =>
      this.client.get(`/vendors/${vendorId}/bookings`, { params }),

    get: (bookingId: string) =>
      this.client.get(`/bookings/${bookingId}`),

    create: (data: any) =>
      this.client.post('/bookings', data),

    cancel: (bookingId: string, reason?: string) =>
      this.client.post(`/bookings/${bookingId}/cancel`, { reason }),

    amend: (bookingId: string, data: any) =>
      this.client.post(`/bookings/${bookingId}/amend`, data),

    updateStatus: (bookingId: string, status: string) =>
      this.client.put(`/bookings/${bookingId}/status`, { status }),

    resendConfirmation: (bookingId: string) =>
      this.client.post(`/bookings/${bookingId}/resend`),

    createHold: (data: any) =>
      this.client.post('/bookings/hold', data),

    refund: (bookingId: string, data?: { amount?: number; reason: string }) =>
      this.client.post(`/bookings/${bookingId}/refund`, data),
  }

  // Vendors endpoints
  vendors = {
    list: (params?: { page?: number; limit?: number; status?: string; search?: string; sortBy?: string; sortOrder?: string }) =>
      this.client.get('/vendors', { params }),

    get: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}`),

    create: (data: any) =>
      this.client.post('/vendors', data),

    update: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}`, data),

    // Domain / Username
    getDomain: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/domain`),

    setUsername: (vendorId: string, username: string) =>
      this.client.put(`/vendors/${vendorId}/domain`, { type: 'username', value: username }),

    setCustomDomain: (vendorId: string, domain: string) =>
      this.client.put(`/vendors/${vendorId}/domain`, { type: 'custom', value: domain }),

    removeCustomDomain: (vendorId: string) =>
      this.client.delete(`/vendors/${vendorId}/domain`),

    getCheckoutConfig: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/checkout/config`),


    updateTracking: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}/checkout/tracking`, data),

    updateSEO: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}/checkout/seo`, data),

    // Storefront v3 identity (iter-3b): legal/trading/founder + destinations/languages/press.
    updateIdentity: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}/checkout/identity`, data),

    // Storefront v3 content (iter-3c): descriptions, hero, pricing mode, FAQs.
    updateContent: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}/checkout/content`, data),

    // Dedicated FAQ editor endpoint (iter-9f). Enforces auto-FAQ floor and
    // promotes source = 'auto' -> 'vendor' on any text change.
    updateFaqs: (
      vendorId: string,
      data: {
        faqs: Array<{
          question: string
          answer: string
          displayOrder: number
          isVisible: boolean
        }>
      },
    ) => this.client.put(`/vendors/${vendorId}/faqs`, data),

    // Super-admin only (iter-9f). Re-derives FAQs from Bokun source_data
    // and resets the auto-floor to the regenerated count.
    regenerateFaqs: (vendorId: string) =>
      this.client.post(`/vendors/${vendorId}/faqs/regenerate`),

    // Stripe Connect
    connectStripe: (
      vendorId: string,
      data: {
        email: string;
        country: string;
        legalEntityType?: 'individual' | 'company';
        returnUrl?: string;
        refreshUrl?: string;
      },
    ) =>
      this.client.post(`/vendors/${vendorId}/stripe/connect`, data),

    getStripeStatus: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/stripe/status`),

    refreshStripeOnboarding: (vendorId: string, data?: { returnUrl?: string; refreshUrl?: string }) =>
      this.client.post(`/vendors/${vendorId}/stripe/refresh`, data),

    disconnectStripe: (vendorId: string) =>
      this.client.delete(`/vendors/${vendorId}/stripe/account`),

    // Subscriptions
    subscribe: (vendorId: string, data: { planId: string; psp?: string }) =>
      this.client.post(`/vendors/${vendorId}/subscription`, data),

    getSubscriptionStatus: (vendorId: string, params?: { sync?: boolean }) =>
      this.client.get(`/vendors/${vendorId}/subscription/status`, { params }),

    getAvailablePlans: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/subscription/plans`),

    cancelSubscription: (vendorId: string, data?: { immediately?: boolean; reason?: string }) =>
      this.client.post(`/vendors/${vendorId}/subscription/cancel`, data),

    changeSubscription: (vendorId: string, data: { planId: string }) =>
      this.client.post(`/vendors/${vendorId}/subscription/change`, data),

    getFeedConfiguration: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/feed-configuration`),

    updateFeedConfiguration: (vendorId: string, data: any) =>
      this.client.put(`/vendors/${vendorId}/feed-configuration`, data),

    createFeedApiKey: (vendorId: string, data: { name: string; description?: string }) =>
      this.client.post(`/vendors/${vendorId}/api-keys/feed`, data),

    createWidgetApiKey: (vendorId: string, data: { name: string; description?: string }) =>
      this.client.post(`/vendors/${vendorId}/api-keys/widget`, data),

    getSubscriptionTransactions: (vendorId: string, params?: { page?: number; limit?: number; transaction_type?: string; status?: string }) =>
      this.client.get(`/vendors/${vendorId}/subscriptions/transactions`, { params }),

    getTrustScore: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/trust-score`),

    // Supplier Agreement
    getSupplierAgreement: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/supplier-agreement`),

    acceptSupplierAgreement: (vendorId: string, version: string) =>
      this.client.post(`/vendors/${vendorId}/supplier-agreement`, { version }),

    // Onboarding
    getOnboardingStatus: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/onboarding-status`),

    // Team management
    team: {
      list: (vendorId: string) =>
        this.client.get(`/vendors/${vendorId}/team`),

      invite: (vendorId: string, data: any) =>
        this.client.post(`/vendors/${vendorId}/team/invite`, data),

      updateRole: (vendorId: string, userId: string, role: string) =>
        this.client.put(`/vendors/${vendorId}/team/${userId}/role`, { role }),

      remove: (vendorId: string, userId: string) =>
        this.client.delete(`/vendors/${vendorId}/team/${userId}`),
    },

    // OAuth clients (legacy — use aiConnections instead)
    oauthClients: {
      list: (vendorId: string) =>
        this.client.get(`/vendors/${vendorId}/oauth-clients`),

      create: (vendorId: string, data: { name: string; description?: string; redirect_uris?: string[] }) =>
        this.client.post(`/vendors/${vendorId}/oauth-clients`, data),

      revoke: (vendorId: string, clientId: string) =>
        this.client.delete(`/vendors/${vendorId}/oauth-clients/${clientId}`),
    },

    // AI Connections (unified API key + OAuth client)
    aiConnections: {
      list: (vendorId: string) =>
        this.client.get(`/vendors/${vendorId}/ai-connections`),

      create: (vendorId: string, data: { name: string; description?: string; redirect_uris?: string[] }) =>
        this.client.post(`/vendors/${vendorId}/ai-connections`, data),

      revoke: (vendorId: string, clientId: string) =>
        this.client.delete(`/vendors/${vendorId}/ai-connections/${clientId}`),

      getOpenApiSchema: (vendorId: string) =>
        this.client.get(`/vendors/${vendorId}/openapi-schema`),

      getChatgptShowcase: (vendorId: string) =>
        this.client.get(`/vendors/${vendorId}/chatgpt-showcase`),

      updateChatgptShowcase: (
        vendorId: string,
        data: { chatgptAppUrl?: string | null; customGptUrl?: string | null }
      ) => this.client.put(`/vendors/${vendorId}/chatgpt-showcase`, data),
    },
  }

  // Paid add-ons (iter-12a). Two named carve-outs from the Everything-Free rule:
  // ai_booking_agent (Track 1) and booking_widget_embed (Track 3).
  addOns = {
    // Public add-on catalogue.
    list: () => this.client.get('/add-ons'),

    // Per-vendor activation state (catalogue joined to vendor_add_ons).
    getForVendor: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/add-ons`),

    // Activate a paid add-on. Enforces tier gate. Idempotent.
    activate: (vendorId: string, slug: string) =>
      this.client.post(`/vendors/${vendorId}/add-ons/${slug}/activate`),

    // Cancel an active paid add-on. Idempotent.
    cancel: (vendorId: string, slug: string) =>
      this.client.post(`/vendors/${vendorId}/add-ons/${slug}/cancel`),

    // AI Website Booking Agent settings (iter-12d). Add-on must be active.
    // GET never returns the plaintext OpenAI key - only has_openai_api_key.
    getAgentSettings: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/add-ons/ai-booking-agent/settings`),

    updateAgentSettings: (
      vendorId: string,
      payload: {
        openai_api_key?: string | null
        greeting?: string | null
        custom_instructions?: string | null
        public_support_email?: string | null
        public_support_phone?: string | null
        suggested_prompts?: string[] | null
        tone?: 'friendly' | 'professional' | 'concise' | null
      }
    ) =>
      this.client.put(
        `/vendors/${vendorId}/add-ons/ai-booking-agent/settings`,
        payload
      ),

    // Agent API keys (iter-12e). Mint / list / revoke the public keys the
    // embeddable chat widget uses to call /v1/agents/*. The raw key is
    // returned once on create, then never again.
    createAgentApiKey: (
      vendorId: string,
      payload: { name: string; description?: string }
    ) =>
      this.client.post(
        `/vendors/${vendorId}/api-keys/agent`,
        payload
      ),

    listAgentApiKeys: (vendorId: string) =>
      this.client.get(`/vendors/${vendorId}/api-keys/agent`),

    revokeAgentApiKey: (vendorId: string, apiKeyId: number | string) =>
      this.client.post(
        `/vendors/${vendorId}/api-keys/agent/${apiKeyId}/revoke`
      ),

    // Agent session transcripts (iter-12f). Backoffice reads only. The list
    // endpoint returns a lightweight projection; the detail endpoint returns
    // the full messages array.
    listAgentSessions: (
      vendorId: string,
      params: { limit?: number; offset?: number; handoffs?: number } = {}
    ) =>
      this.client.get(
        `/vendors/${vendorId}/add-ons/ai-booking-agent/sessions`,
        { params }
      ),

    getAgentSession: (vendorId: string, sessionId: string) =>
      this.client.get(
        `/vendors/${vendorId}/add-ons/ai-booking-agent/sessions/${sessionId}`
      ),
  }

  // Payments endpoints
  payments = {
    createIntent: (data: any) =>
      this.client.post('/payments/intent', data),

    confirm: (paymentId: string) =>
      this.client.post(`/payments/${paymentId}/confirm`),

    refund: (paymentId: string, data?: any) =>
      this.client.post(`/payments/${paymentId}/refund`, data),
  }

  // Admin endpoints
  admin = {
    // Dashboard
    getSystemStats: () =>
      this.client.get('/admin/dashboard/stats'),

    thingsToDo: {
      /** Start async live-From export; returns 202 with { jobId }. */
      startExport: () =>
        this.client.get('/admin/things-to-do/csv', {
          params: { _: Date.now() },
        }),

      startVendorExport: (vendorId: string) =>
        this.client.get(`/admin/vendors/${vendorId}/things-to-do/csv`, {
          params: { _: Date.now() },
        }),

      /** Poll job; when ready the response is a CSV blob. */
      getExportJob: (jobId: string) =>
        this.client.get(`/admin/things-to-do/csv/jobs/${jobId}`, {
          responseType: 'blob',
          params: { _: Date.now() },
        }),
    },

    listAllBookings: (params?: any) =>
      this.client.get('/admin/dashboard/bookings', { params }),

    listAllPayments: (params?: any) =>
      this.client.get('/admin/dashboard/payments', { params }),

    // Vendors
    vendors: {
      updateStatus: (vendorId: string, status: string, reason?: string) =>
        this.client.put(`/admin/vendors/${vendorId}/status`, { status, reason }),

      activate: (vendorId: string) =>
        this.client.post(`/admin/vendors/${vendorId}/activate`),

      suspend: (vendorId: string, reason?: string) =>
        this.client.post(`/admin/vendors/${vendorId}/suspend`, { reason }),

      delete: (vendorId: string) =>
        this.client.delete(`/admin/vendors/${vendorId}`),

      configureFees: (vendorId: string, data: any) =>
        this.client.put(`/admin/vendors/${vendorId}/fees`, data),

      getFees: (vendorId: string) =>
        this.client.get(`/admin/vendors/${vendorId}/fees`),

      assignFeePlan: (vendorId: string, feePlanId: number | null) =>
        this.client.put(`/admin/vendors/${vendorId}/fee-plan`, { fee_plan_id: feePlanId }),

      lockDomain: (vendorId: string, locked: boolean, reason?: string) =>
        this.client.post(`/admin/vendors/${vendorId}/domain/lock`, { locked, reason }),

      // iter-8b: super-admin domain set/remove. Hits the existing
      // consolidated /vendors/{vendorId}/domain endpoint, which permits
      // super_admin tenant to act on any vendor.
      setCustomDomain: (vendorId: string, domain: string) =>
        this.client.put(`/vendors/${vendorId}/domain`, { type: 'custom', value: domain }),

      removeCustomDomain: (vendorId: string) =>
        this.client.delete(`/vendors/${vendorId}/domain`),

      impersonate: (vendorId: string) =>
        this.client.post(`/admin/vendors/${vendorId}/impersonate`),

      // iter-8a: storefront v3 upgrade flow
      getStorefrontV3Status: (vendorId: string) =>
        this.client.get(`/admin/vendors/${vendorId}/storefront-v3-status`),

      applyStorefrontV3: (vendorId: string, dryRun: boolean) =>
        this.client.post(`/admin/vendors/${vendorId}/storefront-v3`, { dry_run: dryRun }),
    },

    // Products
    products: {
      listAll: (params?: any) =>
        this.client.get('/admin/products', { params }),

      update: (productId: string, data: any) =>
        this.client.put(`/admin/products/${productId}`, data),

      delete: (productId: string) =>
        this.client.delete(`/admin/products/${productId}`),
    },

    // Subscriptions
    subscriptions: {
      listPlans: () =>
        this.client.get('/admin/subscriptions/plans'),

      createPlan: (data: any) =>
        this.client.post('/admin/subscriptions/plans', data),

      updatePlan: (planId: string, data: any) =>
        this.client.put(`/admin/subscriptions/plans/${planId}`, data),

      deactivatePlan: (planId: string) =>
        this.client.put(`/admin/subscriptions/plans/${planId}`, { active: false }),
    },

    pricingTiers: {
      list: () =>
        this.client.get('/admin/pricing-tiers'),

      create: (data: any) =>
        this.client.post('/admin/pricing-tiers', data),

      update: (tierId: string, data: any) =>
        this.client.put(`/admin/pricing-tiers/${tierId}`, data),

      updateSettings: (annualDiscountPercent: number) =>
        this.client.put('/admin/pricing-catalog/settings', {
          annual_discount_percent: annualDiscountPercent,
        }),

      createFeatureRow: (data: any) =>
        this.client.post('/admin/pricing-feature-rows', data),

      updateFeatureRows: (rows: any[]) =>
        this.client.put('/admin/pricing-feature-rows', { rows }),

      deleteFeatureRow: (rowId: string) =>
        this.client.delete(`/admin/pricing-feature-rows/${rowId}`),
    },

    // Users
    users: {
      list: () =>
        this.client.get('/admin/users'),

      invite: (data: any) =>
        this.client.post('/admin/users/invite', data),

      updateRole: (userId: string, role: string) =>
        this.client.put(`/admin/users/${userId}/role`, { role }),

      remove: (userId: string) =>
        this.client.delete(`/admin/users/${userId}`),

      resendInvite: (userId: string) =>
        this.client.post(`/admin/users/${userId}/resend-invite`),
    },

    // Features
    features: {
      list: (status?: string) =>
        this.client.get('/admin/features', { params: { status } }),

      create: (data: { name: string; slug: string; description?: string; tier: number }) =>
        this.client.post('/admin/features', data),

      update: (featureId: string, data: any) =>
        this.client.put(`/admin/features/${featureId}`, data),

      deactivate: (featureId: string) =>
        this.client.put(`/admin/features/${featureId}`, { status: 'inactive' }),
    },

    // Fee Plans
    feePlans: {
      list: (status?: string) =>
        this.client.get('/admin/fees/plans', { params: { status } }),

      create: (data: { name: string; description?: string; percentage_fee: number; fixed_fee?: number; currency?: string; min_fee?: number; max_fee?: number; is_default?: boolean }) =>
        this.client.post('/admin/fees/plans', data),

      update: (planId: string, data: any) =>
        this.client.put(`/admin/fees/plans/${planId}`, data),

      setDefault: (planId: string) =>
        this.client.put(`/admin/fees/plans/${planId}/default`),

      deactivate: (planId: string) =>
        this.client.put(`/admin/fees/plans/${planId}`, { status: 'inactive' }),
    },

    // Providers
    providers: {
      list: (status?: string) =>
        this.client.get('/admin/providers', { params: { status } }),

      create: (data: { name: string; slug: string; fee_rate: number; description?: string; status?: string }) =>
        this.client.post('/admin/providers', data),

      update: (providerId: string, data: any) =>
        this.client.put(`/admin/providers/${providerId}`, data),

      get: (providerId: string) =>
        this.client.get(`/admin/providers/${providerId}`),

      setDefault: (providerId: string) =>
        this.client.put(`/admin/providers/${providerId}/default`),

      deactivate: (providerId: string) =>
        this.client.put(`/admin/providers/${providerId}`, { status: 'inactive' }),
    },

    // API Keys
    apiKeys: {
      list: (params?: { vendor_id?: string; status?: string }) =>
        this.client.get('/admin/api-keys', { params }),

      create: (data: { name: string; description?: string; vendor_id?: number; api_groups: string[]; expires_at?: string }) =>
        this.client.post('/admin/api-keys', data),

      revoke: (keyId: string) =>
        this.client.delete(`/admin/api-keys/${keyId}`),
    },

    // AI Connections
    aiConnections: {
      list: (params?: { scope?: string }) =>
        this.client.get('/admin/ai-connections', { params }),

      create: (data: { name: string; description?: string; vendor_id?: number; redirect_uris?: string[] }) =>
        this.client.post('/admin/ai-connections', data),

      revoke: (clientId: string) =>
        this.client.delete(`/admin/ai-connections/${clientId}`),
    },

    chatgptShowcase: {
      get: () => this.client.get('/admin/chatgpt-showcase'),

      update: (data: {
        mode?: 'platform' | 'vendor';
        chatgptAppUrl?: string | null;
        customGptUrl?: string | null;
      }) => this.client.put('/admin/chatgpt-showcase', data),
    },

    // Reports
    generateVendorReport: (vendorId: string) =>
      this.client.get(`/admin/reports/vendors/${vendorId}`),
  }

  // Checkout (OpenAI Agentic Checkout)
  checkout = {
    createSession: (data: any) =>
      this.client.post('/checkout_sessions', data),

    getSession: (sessionId: string) =>
      this.client.get(`/checkout_sessions/${sessionId}`),

    updateSession: (sessionId: string, data: any) =>
      this.client.post(`/checkout_sessions/${sessionId}`, data),

    completeSession: (sessionId: string) =>
      this.client.post(`/checkout_sessions/${sessionId}/complete`),

    cancelSession: (sessionId: string) =>
      this.client.post(`/checkout_sessions/${sessionId}/cancel`),
  }

  // Analytics endpoints
  // Inventory Feed endpoints (Canonical Product Feed)
  inventory = {
    createFeed: (data: any) =>
      this.client.post('/inventory/feeds', data),

    getFeed: (feedId?: string, vendorId?: string) => {
      if (feedId) {
        return this.client.get(`/inventory/feeds/${feedId}`)
      }
      const params = vendorId ? { vendorId } : {}
      return this.client.get('/inventory/feeds', { params })
    },

    listFeeds: (params?: { vendorId?: string; status?: string; limit?: number; offset?: number }) =>
      this.client.get('/inventory/feeds/list', { params }),

    updateFeed: (feedId: string, data: any) =>
      this.client.put(`/inventory/feeds/${feedId}`, data),

    deleteFeed: (feedId: string) =>
      this.client.delete(`/inventory/feeds/${feedId}`),

    syncFeed: (data: { platform: string; vendorId?: number }) =>
      this.client.post('/inventory/feeds/sync', data),

    syncExperiences: (feedId: string, data?: { vendorId?: number }) =>
      this.client.post(`/inventory/feeds/${feedId}/sync-experiences`, data || {}),

    listProducts: (feedId: string, params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }) =>
      this.client.get(`/inventory/feeds/${feedId}/products`, { params }),

    getProduct: (productId: string) =>
      this.client.get(`/inventory/products/${productId}`),

    updateProduct: (productId: string, data: any) =>
      this.client.put(`/inventory/products/${productId}`, data),

    setProductStatus: (productId: string, status: 'active' | 'inactive') =>
      this.client.patch(`/inventory/products/${productId}/status`, { status }),

    // Dedicated per-product FAQ editor endpoint (iter-9b/iter-9c). Enforces
    // the auto-FAQ floor server-side and promotes source = 'auto' -> 'vendor'
    // on any text change at the same display_order.
    updateFaqs: (
      productId: string,
      data: {
        faqs: Array<{
          question: string
          answer: string
          displayOrder: number
          isVisible: boolean
        }>
      },
    ) => this.client.put(`/inventory/products/${productId}/faqs`, data),

    // Super-admin only (iter-9b/iter-9c). Re-derives per-product FAQs and
    // ratchets the auto-floor to max(currentFloor, newVisibleAutoCount).
    regenerateFaqs: (productId: string) =>
      this.client.post(`/inventory/products/${productId}/faqs/regenerate`),
  }

  // Backoffice ledger endpoints
  ledger = {
    getSummary: (params?: { start_date?: string; end_date?: string }) =>
      this.client.get('/backoffice/ledger/summary', { params }),
  }

  // Backoffice reservations endpoints
  reservations = {
    list: (params?: { page?: number; limit?: number; status?: string; start_date?: string; end_date?: string; search?: string }) =>
      this.client.get('/backoffice/reservations', { params }),

    get: (id: string) =>
      this.client.get(`/backoffice/reservations/${id}`),

    cancel: (id: string) =>
      this.client.post(`/backoffice/reservations/${id}/cancel`),

    refund: (id: string, data?: { amount?: number; reason?: string }) =>
      this.client.post(`/backoffice/reservations/${id}/refund`, data),
  }

  // Analytics endpoints
  analytics = {
    track: (data: { event_type: string; vendor_id: number; product_id?: number; source?: string; metadata?: any }) =>
      this.client.post('/analytics/track', data),

    getOverview: (vendorId: string, params?: Record<string, any>) =>
      this.client.get(`/vendors/${vendorId}/analytics/overview`, { params }),

    getFunnel: (vendorId: string, params?: Record<string, any>) =>
      this.client.get(`/vendors/${vendorId}/analytics/funnel`, { params }),

    getAIConversion: (vendorId: string, params?: Record<string, any>) =>
      this.client.get(`/vendors/${vendorId}/analytics/ai-conversion`, { params }),
    getAttribution: (vendorId: string, params?: Record<string, any>) =>
      this.client.get(`/vendors/${vendorId}/analytics/attribution`, { params }),

    getReliability: (vendorId: string, params?: Record<string, any>) =>
      this.client.get(`/vendors/${vendorId}/analytics/reliability`, { params }),
  }

  // Integrations endpoints
  integrations = {
    // Generic Integration Connection
    connectIntegration: (data: { platform_name: string; platform_merchant_id: string; vendor_id?: number }) =>
      this.client.post('/integrations/connect', data),

    // Get integration status by vendor and platform
    getIntegration: (vendorId: string, platform: string) =>
      this.client.get(`/integrations/${vendorId}/${platform}`),

    // Disconnect/delete integration by vendor and platform
    disconnectIntegration: (vendorId: string, platform: string) =>
      this.client.delete(`/integrations/${vendorId}/${platform}`),

    // Bokun Integration (using generic endpoints)
    getBokunIntegration: (vendorId: string) =>
      this.client.get(`/integrations/${vendorId}/bokun`),

    // Sync feeds from external platform (Bokun, Viator, etc.)
    syncBokunFeeds: (vendorId: string) =>
      this.client.post('/inventory/feeds/sync', { platform: 'bokun', vendorId: parseInt(vendorId, 10) }),
  }
}

export const api = new ApiService()
export default api

